/**
 * RedirectPathService - Manages redirect path persistence using localStorage
 */

const REDIRECT_PATH_KEY = 'liff_redirect_path';
const REDIRECT_PATH_TIMESTAMP_KEY = 'liff_redirect_path_timestamp';
const REDIRECT_PATH_TTL = 10 * 60 * 1000; // 10 minutes TTL

export class RedirectPathService {
  /**
   * Validates if a path is allowed for redirection
   * @param {string} path - The path to validate
   * @returns {boolean} - Whether the path is valid
   */
  static isValidPath(path) {
    const validPaths = ['/profile', '/calendar', '/income'];
    if (!path) return false;
    try {
      const url = new URL(path, window.location.origin);
      const pathname = url.pathname.startsWith('/') ? url.pathname : `/${url.pathname}`;
      return validPaths.includes(pathname);
    } catch (e) {
      const [pathname] = path.split('?');
      const normalized = pathname.startsWith('/') ? pathname : `/${pathname}`;
      return validPaths.includes(normalized);
    }
  }

  /**
   * Normalizes a path by ensuring it starts with '/'
   * @param {string} path - The path to normalize
   * @returns {string} - The normalized path
   */
  static normalizePath(path) {
    if (!path) return null;
    try {
      const url = new URL(path, window.location.origin);
      const pathname = url.pathname.startsWith('/') ? url.pathname : `/${url.pathname}`;
      return `${pathname}${url.search}`;
    } catch (e) {
      const hasQuery = path.includes('?');
      const [pathname, query] = path.split('?');
      const normalized = pathname.startsWith('/') ? pathname : `/${pathname}`;
      return hasQuery ? `${normalized}?${query}` : normalized;
    }
  }

  /**
   * Sets the redirect path in localStorage with timestamp
   * @param {string} path - The path to store
   * @returns {boolean} - Whether the path was successfully stored
   */
  static setRedirectPath(path) {
    try {
      if (!this.isValidPath(path)) {
        return false;
      }

      const normalizedPath = this.normalizePath(path);
      const timestamp = Date.now();

      localStorage.setItem(REDIRECT_PATH_KEY, normalizedPath);
      localStorage.setItem(REDIRECT_PATH_TIMESTAMP_KEY, timestamp.toString());
      
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Gets the redirect path from localStorage if it's still valid
   * @param {string} fallback - Fallback path if no valid stored path exists
   * @returns {string} - The redirect path or fallback
   */
  static getRedirectPath(fallback = '/calendar') {
    try {
      const storedPath = localStorage.getItem(REDIRECT_PATH_KEY);
      const storedTimestamp = localStorage.getItem(REDIRECT_PATH_TIMESTAMP_KEY);

      if (!storedPath || !storedTimestamp) {
        return fallback;
      }

      const timestamp = parseInt(storedTimestamp, 10);
      const now = Date.now();
      const age = now - timestamp;

      if (age > REDIRECT_PATH_TTL) {
        this.clearRedirectPath();
        return fallback;
      }

      if (!this.isValidPath(storedPath)) {
        this.clearRedirectPath();
        return fallback;
      }
      
      return storedPath;
    } catch (error) {
      return fallback;
    }
  }

  /**
   * Clears the stored redirect path and timestamp
   */
  static clearRedirectPath() {
    try {
      localStorage.removeItem(REDIRECT_PATH_KEY);
      localStorage.removeItem(REDIRECT_PATH_TIMESTAMP_KEY);
    } catch (error) {
    }
  }




  /**
   * Gets debug information about the current stored path
   * @returns {object} - Debug information
   */
  static getDebugInfo() {
    try {
      const storedPath = localStorage.getItem(REDIRECT_PATH_KEY);
      const storedTimestamp = localStorage.getItem(REDIRECT_PATH_TIMESTAMP_KEY);
      const timestamp = storedTimestamp ? parseInt(storedTimestamp, 10) : null;
      const now = Date.now();
      const age = timestamp ? now - timestamp : null;

      return {
        storedPath,
        timestamp,
        age,
        ageInMinutes: age ? Math.round(age / 60000) : null,
        ttlInMinutes: Math.round(REDIRECT_PATH_TTL / 60000),
        isExpired: age ? age > REDIRECT_PATH_TTL : null,
        isValid: storedPath ? this.isValidPath(storedPath) : false,
        effectivePath: this.getRedirectPath()
      };
    } catch (error) {
      return { error: error.message };
    }
  }
}

export default RedirectPathService;